/**
 * useDirtyTracking - Hook for tracking unsaved changes in scenario form
 *
 * Provides snapshot-based dirty tracking by comparing current form state
 * to the last saved state. Consolidates isDirty calculation and snapshot
 * capture to avoid field list duplication.
 *
 * @module hooks/useDirtyTracking
 */

import { useMemo, useCallback, useState } from 'react';
import { debug } from '../utils/debug';

/**
 * Fields tracked for dirty detection
 * IMPORTANT: Keep this list in sync with what's saved to backend
 */
const TRACKED_FIELDS = [
    'scenarioName',
    'siteName',
    'zipCode',
    'street',
    'city',
    'state',
    'usePv',
    'pvMaxKw',
    'pvCost',
    'useStorage',
    'storageMaxKw',
    'storageMaxKwh',
    'useGenerator',
    'generatorMaxKw',
    'blendedRate',
    'offGrid',
    'gasTariffId',
    'gasTariffData',
];

/**
 * Extract snapshot from scenarioForm using tracked fields
 * @param {Object} scenarioForm - The scenario form state object
 * @returns {Object} Snapshot object with tracked fields
 */
function extractSnapshot(scenarioForm) {
    const snapshot = {};
    for (const field of TRACKED_FIELDS) {
        snapshot[field] = scenarioForm[field];
    }
    return snapshot;
}

/**
 * Hook for tracking dirty state in scenario forms
 *
 * @param {Object} scenarioForm - The scenario form state object
 * @returns {Object} Object with isDirty, captureStateSnapshot, lastSavedSnapshot state
 */
export function useDirtyTracking(scenarioForm) {
    const [lastSavedSnapshot, setLastSavedSnapshot] = useState(null);

    // Calculate isDirty by comparing current state to last saved snapshot
    const isDirty = useMemo(() => {
        if (!lastSavedSnapshot) return false;

        const currentSnapshot = JSON.stringify(extractSnapshot(scenarioForm));
        return currentSnapshot !== lastSavedSnapshot;
    }, [
        // Include all tracked fields as dependencies
        scenarioForm.scenarioName,
        scenarioForm.siteName,
        scenarioForm.zipCode,
        scenarioForm.street,
        scenarioForm.city,
        scenarioForm.state,
        scenarioForm.usePv,
        scenarioForm.pvMaxKw,
        scenarioForm.pvCost,
        scenarioForm.useStorage,
        scenarioForm.storageMaxKw,
        scenarioForm.storageMaxKwh,
        scenarioForm.useGenerator,
        scenarioForm.generatorMaxKw,
        scenarioForm.blendedRate,
        scenarioForm.offGrid,
        scenarioForm.gasTariffId,
        scenarioForm.gasTariffData,
        lastSavedSnapshot,
    ]);

    // Capture current state as a snapshot
    const captureStateSnapshot = useCallback(() => {
        const snapshot = extractSnapshot(scenarioForm);
        debug.log('[useDirtyTracking] Captured state snapshot:', snapshot);
        return snapshot;
    }, [scenarioForm]);

    // Mark current state as saved (updates lastSavedSnapshot)
    const markAsSaved = useCallback(() => {
        const snapshot = extractSnapshot(scenarioForm);
        setLastSavedSnapshot(JSON.stringify(snapshot));
        debug.log('[useDirtyTracking] Marked as saved');
    }, [scenarioForm]);

    // Initialize with a specific snapshot (for loading saved scenarios)
    const initializeSnapshot = useCallback((snapshot) => {
        if (typeof snapshot === 'string') {
            setLastSavedSnapshot(snapshot);
        } else {
            setLastSavedSnapshot(JSON.stringify(snapshot));
        }
        debug.log('[useDirtyTracking] Initialized snapshot');
    }, []);

    return {
        isDirty,
        captureStateSnapshot,
        markAsSaved,
        initializeSnapshot,
        lastSavedSnapshot,
        setLastSavedSnapshot, // For direct manipulation if needed
    };
}

export default useDirtyTracking;
